import type { FormikProps } from 'formik';
import { Field, FieldProps, Formik } from 'formik';
import React, { useCallback, useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import styled from 'styled-components';
import * as Yup from 'yup';

import awsmobile from '../config/aws-exports';
import { useBrand } from '../context/BrandProvider';
import withHookedTheme from '../hocs/withHookedTheme';
import type { Theme } from '../theme/themes/ThemeTypes';
import ErrorLabel from '../ui/ErrorLabel';
import LoginLayout from '../ui/Layout/LoginLayout';
import LoginCard from '../ui/LoginCard';
import ThemeAwareLogo from '../ui/ThemeAwareLogo';
import apiRequest from '../utils/apiRequest';
import { devHostHint } from '../utils/branding';
import { max } from '../utils/breakpoints';
import { buildRedirectUrl, getCurrentHostPrefix, hasRedirected } from '../utils/domainRedirect';
import ensureError from '../utils/ensureError';

const SignInWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;

  ${max.md} {
    flex-direction: column;
  }

  .signin-button {
    flex-shrink: 0;

    ${max.md} {
      width: 100%;
      margin-top: 0px;
    }
  }
`;

type LoopSignUpProps = {
  theme: Theme;
  authState?: string;
  changeState?: (s: 'signIn' | 'confirmSignUp', data?: { username?: string }) => void;
  onStateChange?: (s: 'signIn' | 'confirmSignUp', data?: { username?: string }) => void;
  error?: (e: unknown) => void;
};

type SignUpFormValues = {
  name: string;
  username: string;
  password: string;
};

// Local helper: normalize unknown errors and safely report via optional handler without throwing
const safeReportError = (handler: ((e: unknown) => void) | undefined, err: unknown): void => {
  try {
    if (typeof handler === 'function') handler(ensureError(err));
  } catch {}
};

const LoopSignUp: React.FC<LoopSignUpProps> = ({ theme, changeState, onStateChange, error: onError, authState }) => {
  const [requestPending, setRequestPending] = useState<boolean>(false);
  const { domainPrefix, installerId, brandResolving } = useBrand();
  const { layout, language, links } = theme;

  // Submit handler assumes installerId has been resolved by the effect above.
  // The submit button remains disabled while brandResolving or when installerId is empty to enforce this contract.
  const signUp = useCallback(
    async (values: SignUpFormValues): Promise<string | undefined> => {
      setRequestPending(true);
      try {
        const installer_id = installerId;
        if (!installer_id) {
          throw new Error(
            domainPrefix
              ? `Installer not resolved for domain "${domainPrefix}" yet.` + devHostHint()
              : `Installer not resolved. Missing domain prefix.` + devHostHint()
          );
        }

        const payload = {
          email: values.username.toLowerCase().trim(),
          password: values.password,
          name: values.name,
          installer_id,
        };

        await apiRequest(`${awsmobile.optimise_endpoint_auth}/onboarding/sign-up`, null, 'POST', payload, {
          auth: false,
        });

        if (typeof onStateChange === 'function') {
          onStateChange('confirmSignUp', { username: payload.email });
        } else if (typeof changeState === 'function') {
          changeState('confirmSignUp', { username: payload.email });
        }
        return undefined;
      } catch (err) {
        safeReportError(onError, err);
        if (err && typeof err === 'object' && 'message' in err && typeof (err as any).message === 'string') {
          return (err as any).message;
        }
        return typeof err === 'string' ? err : 'An error occurred';
      } finally {
        setRequestPending(false);
      }
    },
    [onStateChange, changeState, onError, installerId, domainPrefix]
  );

  const signUpValidationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    username: Yup.string().email('Invalid email address').required('Email is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(/[a-z]/, 'Password must contain a lowercase letter')
      .matches(/[A-Z]/, 'Password must contain an uppercase letter')
      .matches(/[0-9]/, 'Password must contain a number')
      .required('Password is required'),
  });

  // Only render this screen when Amplify auth state is signUp (or the transitional signedUp)
  if (authState !== 'signUp' && authState !== 'signedUp') {
    return null;
  }

  // Extracted submit handler
  const handleSignUpSubmit = async (
    values: SignUpFormValues,
    setSubmitting: (s: boolean) => void,
    setStatus: (msg: string | undefined) => void
  ): Promise<void> => {
    setSubmitting(true);
    try {
      const errorMessage = await signUp({
        name: values.name,
        username: values.username.toLowerCase().trim(),
        password: values.password,
      });

      setStatus(errorMessage);

      if (!errorMessage) {
        const expectedPrefix = domainPrefix;
        const currentPrefix = getCurrentHostPrefix();

        if (expectedPrefix && !brandResolving) {
          // If we've already been redirected (query param present), do not redirect again
          if (!hasRedirected() && expectedPrefix !== currentPrefix) {
            window.location.href = buildRedirectUrl(expectedPrefix, '/login', {
              redirected: 'signUp',
              email: values.username.toLowerCase().trim(),
            });
            return;
          }
        }

        if (typeof onStateChange === 'function') {
          onStateChange('confirmSignUp', { username: values.username.toLowerCase().trim() });
        } else if (typeof changeState === 'function') {
          changeState('confirmSignUp', { username: values.username.toLowerCase().trim() });
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LoginLayout>
      <Formik<SignUpFormValues>
        initialValues={{ name: '', username: '', password: '' }}
        validationSchema={signUpValidationSchema}
        onSubmit={async (values, { setSubmitting, setStatus }) => handleSignUpSubmit(values, setSubmitting, setStatus)}
        validateOnBlur
        validateOnChange
      >
        {({ handleSubmit, errors, isSubmitting, status }: FormikProps<SignUpFormValues>) => {
          const hasFormErrors = Object.keys(errors as Record<string, unknown>).length > 0;
          return (
            <LoginCard>
              {layout.showOrHideElements.auth.signUp.showCompanyLogoInCard && (
                <Row>
                  <Col className="px-lg-3 px-md-2 px-1 d-flex justify-content-center mb-3">
                    <ThemeAwareLogo className="d-sm-block" alt="Company Logo" />
                  </Col>
                </Row>
              )}
              <h1 className="text-center">
                Sign up to
                <br />
                {language.appName}
              </h1>
              <Card.Text>
                {language.appName} will buy and store energy when it's cheap, and sell it back to the grid when it's
                expensive.
              </Card.Text>
              {layout.showOrHideElements.auth.signUp.howDoesOptimiseWorkLink && (
                <Card.Text>
                  <a href={links.howDoesItWork} rel="noreferrer" target="_blank">
                    How does {language.appName} work?
                  </a>
                </Card.Text>
              )}
              <Card.Body className="p-0">
                <Container className="text-left">
                  <Form noValidate onSubmit={handleSubmit}>
                    <Row>
                      <Col className="px-lg-3 px-md-2 px-1">
                        <Form.Group className="mb-md-3 mb-2">
                          <Form.Label htmlFor="name" className="form-label">
                            Your name
                          </Form.Label>
                          <Field name="name">
                            {({ field, meta }: FieldProps) => (
                              <>
                                <Form.Control
                                  {...field}
                                  id="signup_name"
                                  key="name"
                                  type="text"
                                  placeholder="Your name"
                                  isInvalid={meta.touched && !!meta.error}
                                  style={{ backgroundImage: 'none' }}
                                />
                                {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                              </>
                            )}
                          </Field>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col className="px-lg-3 px-md-2 px-1">
                        <Form.Group className="mb-md-3 mb-2">
                          <Form.Label htmlFor="username" className="form-label">
                            Email address
                          </Form.Label>
                          <Field name="username">
                            {({ field, meta }: FieldProps) => {
                              const statusMessage = typeof status === 'string' ? status : undefined;
                              const errorMsg = meta.error && meta.touched ? meta.error : statusMessage;

                              return (
                                <>
                                  <Form.Control
                                    {...field}
                                    id="signup_username"
                                    key="username"
                                    type="email"
                                    placeholder="Email address"
                                    onChange={(e) => {
                                      e.target.value = e.target.value.toLowerCase().trim();
                                      field.onChange(e);
                                    }}
                                    isInvalid={!!errorMsg}
                                    style={{ backgroundImage: 'none' }}
                                  />
                                  {errorMsg && <ErrorLabel label={errorMsg} />}
                                </>
                              );
                            }}
                          </Field>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col className="px-lg-3 px-md-2 px-1">
                        <Form.Group className="mb-md-3 mb-2">
                          <Form.Label htmlFor="password" className="form-label">
                            Create a password
                          </Form.Label>
                          <Field name="password">
                            {({ field, meta }: FieldProps) => (
                              <>
                                <Form.Control
                                  {...field}
                                  id="signup_password"
                                  key="password"
                                  type="password"
                                  placeholder="Create a password"
                                  isInvalid={meta.touched && !!meta.error}
                                  style={{ backgroundImage: 'none' }}
                                  autoComplete="new-password"
                                />
                                {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                              </>
                            )}
                          </Field>
                          <div className="terms text-left color-light line-height-md">
                            <p>Must be at minimum 8 characters including upper and lower case and a number.</p>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col className="px-lg-3 px-md-2 p-1">
                        <div className="terms text-center mt-3">
                          <p>
                            By clicking on Sign up, you agree to {language.appName}'s{' '}
                            <a href={links.termsAndConditions.link}>{links.termsAndConditions.name}.</a>
                          </p>
                        </div>
                        <div className="terms text-center mt-3">
                          <p>
                            To learn more about how {language.appName} collects, uses, shares and protects your personal
                            data please read {language.appName}'s{' '}
                            <a href={links.privacyPolicy.link}>{links.privacyPolicy.name}.</a>
                          </p>
                        </div>
                        <Button
                          variant="primary"
                          type="submit"
                          block
                          disabled={requestPending || isSubmitting || hasFormErrors || brandResolving || !installerId}
                          className="mb-2"
                        >
                          Sign up
                        </Button>
                      </Col>
                    </Row>
                    <Row>
                      <Col className="px-lg-3 px-md-2 px-1">
                        <Form.Group className="mb-0 mt-1">
                          <hr className="mt-3 mb-4" />
                          <SignInWrapper>
                            <span className="signup-text">Already have a {language.appName} account?</span>
                            <Button
                              type="button"
                              variant="outline-primary"
                              onClick={() => {
                                if (typeof onStateChange === 'function') {
                                  onStateChange('signIn');
                                } else if (typeof changeState === 'function') {
                                  changeState('signIn');
                                }
                              }}
                              className="signin-button px-4"
                            >
                              Sign in
                            </Button>
                          </SignInWrapper>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                </Container>
              </Card.Body>
            </LoginCard>
          );
        }}
      </Formik>
    </LoginLayout>
  );
};

export default withHookedTheme(LoopSignUp);
